import { useEffect, useState } from "react";
import { Lock, Send, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

const fmt = (iso) => new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });

export const ClientNotes = ({ client }) => {
  const [notes, setNotes] = useState(null);
  const [open, setOpen] = useState(false);
  const [body, setBody] = useState("");
  const [saving, setSaving] = useState(false);

  const load = () => api.get(`/clients/${client.id}/notes`).then((r) => setNotes(r.data)).catch(() => setNotes([]));
  useEffect(() => { if (open && notes === null) load(); }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const add = async (e) => {
    e.preventDefault();
    if (!body.trim()) return;
    setSaving(true);
    try { await api.post(`/clients/${client.id}/notes`, { body }); setBody(""); toast.success("Note saved"); load(); } catch (err) { toast.error(errorMessage(err)); } finally { setSaving(false); }
  };
  const remove = async (n) => {
    try { await api.delete(`/clients/${client.id}/notes/${n.id}`); load(); } catch (err) { toast.error(errorMessage(err)); }
  };

  return (
    <div className="w-full mt-2" data-testid={`notes-${client.id}`}>
      <button type="button" onClick={() => setOpen((v) => !v)} className="text-xs font-bold uppercase tracking-widest text-slate-500 hover:text-indigo-700 flex items-center gap-1" data-testid={`notes-toggle-${client.id}`}>
        <Lock className="w-3.5 h-3.5" /> Private notes {notes ? `(${notes.length})` : ""} {open ? "▴" : "▾"}
      </button>
      {open && (
        <div className="mt-2 rounded-2xl bg-amber-50/60 border border-amber-100 p-4 space-y-3">
          <p className="text-xs text-amber-800">Only you can see these notes. They are never shown to the client.</p>
          <form onSubmit={add} className="flex gap-2 items-start" data-testid={`note-form-${client.id}`}>
            <Textarea value={body} onChange={(e) => setBody(e.target.value)} rows={2} placeholder="e.g. Practised 3× this week; anxious about work travel — suggested Calm Breath before meetings." className="bg-white rounded-xl text-sm" data-testid={`note-input-${client.id}`} />
            <Button type="submit" disabled={saving || !body.trim()} className="rounded-xl h-10 bg-indigo-600 hover:bg-indigo-700 text-white" aria-label="Save note" data-testid={`note-save-${client.id}`}><Send className="w-4 h-4" /></Button>
          </form>
          {notes && notes.length > 0 && (
            <ul className="space-y-2" data-testid={`notes-list-${client.id}`}>
              {notes.map((n) => (
                <li key={n.id} className="bg-white rounded-xl px-3 py-2 text-sm border border-amber-100 flex gap-3" data-testid={`note-${n.id}`}>
                  <div className="flex-1 min-w-0"><p className="text-slate-800 whitespace-pre-wrap">{n.body}</p><p className="text-xs text-slate-400 mt-1">{fmt(n.created_at)}</p></div>
                  <button type="button" onClick={() => remove(n)} className="text-slate-400 hover:text-rose-600 shrink-0" aria-label="Delete note" data-testid={`note-delete-${n.id}`}><Trash2 className="w-4 h-4" /></button>
                </li>
              ))}
            </ul>
          )}
          {notes && notes.length === 0 && <p className="text-xs text-slate-500">No notes yet.</p>}
        </div>
      )}
    </div>
  );
};
